import { initErrorReporting, type ErrorReporter } from '@dw/observability';
import { createBrowserPlayer } from './bootstrap.js';
import { loadConfig, loadSentryConfig } from './config.js';
import { keepOfflineCopy } from './offline.js';
import type { Player } from './player.js';
import './styles.css';

/**
 * Browser/WebView entry point. Nothing here decides anything; it reads config,
 * builds the player and starts it. A configuration failure shows the branded
 * "not activated" card, because even a misconfigured bundle must not put an error
 * dialog on a customer-facing TV (invariant 9).
 */

interface OpsHandle {
  readonly player: Player;
  readonly errors: ErrorReporter;
}

declare global {
  interface Window {
    dwPlayer?: OpsHandle;
  }
}

const root = document.getElementById('dw-player');
if (root === null) throw new Error('missing #dw-player mount point');

const config = loadConfig(import.meta.env);

/**
 * dwf-007 §7.8 / decision-log D30 — Sentry for the player bundle.
 *
 * Invariant 7 is why this is safe here: `initErrorReporting` registers two listeners and
 * performs no I/O, so nothing between boot and first frame waits on the network. The first
 * envelope leaves the stick only after something has already thrown.
 *
 * `loadSentryConfig` reads the environment separately from `loadConfig` so that a bundle whose
 * player config failed to parse still reports it: a stick showing the "not activated" card is
 * exactly the state worth a Sentry event. Without a DSN this is a counted, announced no-op.
 */
const sentry = loadSentryConfig(import.meta.env);
const errors = initErrorReporting({
  app: 'player',
  source: window,
  dsn: sentry.dsn,
  environment: sentry.environment,
  release: config.ok ? config.value.appVersion : null,
});

if (!config.ok) {
  for (const problem of config.errors) {
    console.error(JSON.stringify({ level: 'error', event: 'config.invalid', problem }));
  }
  errors.captureMessage('player config invalid', {
    level: 'fatal',
    extra: { problems: config.errors },
  });
  renderStaticCard(root, 'This screen is not activated', 'Contact WaveCast to activate this screen.');
} else {
  void createBrowserPlayer({
    config: config.value,
    root,
    document,
    window,
  }).then(async ({ player, persistent }) => {
    window.dwPlayer = { player, errors };
    if (!persistent) {
      console.warn(JSON.stringify({ level: 'warn', event: 'storage.volatile' }));
    }
    await player.start();
    // After first frame, not before (invariant 7): keep this bundle on the device so the next
    // boot plays its cached loop even with the venue's internet down. Production builds only —
    // the dev server has no `sw.js`.
    if (import.meta.env.PROD) {
      void keepOfflineCopy(navigator, (event, detail) =>
        console.warn(JSON.stringify({ level: 'warn', event, ...detail })),
      );
    }
  });
}

function renderStaticCard(host: HTMLElement, headline: string, help: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'dw-card__body dw-card__body--not_activated';
  const brand = document.createElement('p');
  brand.className = 'dw-card__brand';
  brand.textContent = 'WaveCast';
  const title = document.createElement('h1');
  title.className = 'dw-card__headline';
  title.textContent = headline;
  const detail = document.createElement('p');
  detail.className = 'dw-card__help';
  detail.textContent = help;
  wrap.append(brand, title, detail);
  host.append(wrap);
}
